import React, { useCallback, useContext, useEffect, useState } from "react";
import "./CustomerServicePage.scss";
import { Alert, Box, Button, Grid, IconButton, Typography } from "@mui/material";
import MyContext from "../../contexts/MyContext";
import Logo from "../../static/VL_Logo_CMYK_DarkBlue.png";
import { MyTextField } from "../../components/my-text-field/MyTextField";
import { EmailRegExp, MaxFileSize, PhoneNumberRegExp, SliderSettings, ValidTextRegExp, lngs, VLPersonalDataLink } from "../../Constant";
import { useTranslation } from "react-i18next";
import { MyButton } from "../../components/my-button/MyButton";
import { Delete, AttachFile } from "@mui/icons-material";
import Slider from "react-slick";
import ClientSettingApiService from "../../adapters/xhr/ClientSettingApiService";
import ClaimApiService from "../../adapters/xhr/ClaimApiService";
import { toBase64 } from "../../Utils";
import { getConfirmation } from "../../components/my-confirmation-dialog/MyConfirmationDialog";
import { MyTheme } from "../../infrastructure/MyThemeProvider";
import { MyCheckbox } from "../../components/my-checkbox/MyCheckbox";

export const CustomerServicePage = (props) => {
  const myContext = useContext(MyContext);
  let sliderRef = React.createRef();
  const { t, i18n } = useTranslation();
  const [firstName, setFirstName] = useState("");
  const [firstNameErr, setFirstNameErr] = useState("");
  const [surName, setSurName] = useState("");
  const [surNameErr, setSurNameErr] = useState("");
  const [email, setEmail] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberErr, setPhoneNumberErr] = useState("");
  const [comment, setComment] = useState("");
  const [commentErr, setCommentErr] = useState("");
  const [declareInformationErr, setDeclareInformationErr] = useState("");
  const [error, setError] = useState(null);
  const [attachments, setAttachments] = useState([]);

  const [phoneChanged, setPhoneChanged] = useState(false);
  const [emailChanged, setEmailChanged] = useState(false);

  const MaxNumberOfAttachments = 3;
  const hiddenFileInput = React.useRef(null);

  const handleClick = (event) => {
    hiddenFileInput.current.click();
  };

  const removeAttachment = (fileName) => {
    setAttachments(attachments.filter((item) => item.name !== fileName));
  };

  const handleChangeAttachment = (event) => {
    const fileUploaded = event.target.files[0];
    if (myContext.allowFileUploadExtension.find((extension) => fileUploaded.name.toLowerCase().endsWith(extension.toLowerCase())) == null)
      setError("not_allowed");
    else if (fileUploaded.size > MaxFileSize) setError("your_uploaded_file_too_big");
    else {
      setAttachments((oldArray) => [...oldArray.filter((item) => item.name !== fileUploaded.name), fileUploaded]);
      setError(null);
    }
  };

  useEffect(() => {
    if (emailChanged && email) validateEmail();
    else setEmailErr("");
  }, [email]);

  useEffect(() => {
    if (phoneChanged && phoneNumber) validatePhone();
    else setPhoneNumberErr("");
  }, [phoneNumber]);

  const validateEmail = () => {
    let validEmail = new RegExp(EmailRegExp);
    if (!email.trim()) {
      setEmailErr("email_must_be_specified");
      return false;
    } else if (!validEmail.test(email.trim())) {
      setEmailErr("email_address_is_incorrect");
      return false;
    } else setEmailErr("");
    return true;
  };

  const validatePhone = () => {
    let validPhoneNumber = new RegExp(PhoneNumberRegExp);
    if (!phoneNumber.trim()) {
      setPhoneNumberErr("");
    } else if (!validPhoneNumber.test(phoneNumber.trim())) {
      setPhoneNumberErr("entered_invalid_phonenumber");
      return false;
    } else setPhoneNumberErr("");
    return true;
  };

  const validate = async () => {
    let isValid = true;

    if (!firstName.trim()) {
      setFirstNameErr("first_name_must_be_specified");
      isValid = false;
    } else {
      setFirstNameErr("");
    }

    if (!surName.trim()) {
      setSurNameErr("surname_must_be_specified");
      isValid = false;
    } else {
      setSurNameErr("");
    }

    if (!validateEmail()) isValid = false;

    if (!validatePhone()) isValid = false;

    if (!comment.trim()) {
      setCommentErr("comment_must_be_specified");
      isValid = false;
    } else {
      setCommentErr("");
    }

    if (isValid && !myContext.declareInformation) {
      setDeclareInformationErr("ensure_info");
      isValid = false;
    } else {
      setDeclareInformationErr("");
    }

    if (isValid) {
      let result = await ClaimApiService.createFeedback({
        fullName: `${firstName} ${surName}`,
        email: email,
        mobileNumber: phoneNumber,
        comment: comment,
        attachments: await Promise.all(
          attachments.map(async (attachment) => {
            return {
              file: await toBase64(attachment),
              fileName: attachment.name,
            };
          })
        ),
      });
      if (!result.isSuccess) {
        let message = t(result.errorMessage);
        const confirmed = await getConfirmation({
          message: message,
          theme: MyTheme,
        });
        if (!confirmed) window.location.reload();
      } else {
        myContext.resetState();
        setFirstName("");
        setSurName("");
        setPhoneNumber("");
        setPhoneChanged(false);
        setEmail("");
        setEmailChanged(false);
        setComment("");
        setAttachments([]);
        myContext.changeCaseNumber(result.data);
        myContext.changeActiveSlide((myContext.activeSlide += 1));
      }
    }
  };

  const getAllowedFileExtension = useCallback(async () => {
    let response = await ClientSettingApiService.GetAllowedFileExtension();
    if (response.isSuccess) {
      myContext.changeAllowFileUploadExtension(response.data);
    }
  }, []);

  useEffect(() => {
    sliderRef.slickGoTo(myContext.activeSlide, false);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [myContext.activeSlide]);

  useEffect(() => {
    if (!myContext.allowFileUploadExtension || myContext.allowFileUploadExtension.length === 0) getAllowedFileExtension();
  }, []);

  return (
    <MyContext.Consumer>
      {(context) => (
        <Grid container className="container custom padding top-2 bottom-2 left-1 right-1" justifyContent="center">
          <Grid item>
            <div hidden={context.activeSlide !== 0}>
              <img src={Logo} className="logo" alt="VL, logo" />
            </div>
            <Slider ref={(slider) => (sliderRef = slider)} {...SliderSettings} beforeChange={(_, next) => context.changeActiveSlide(next)}>
              <div hidden={context.activeSlide !== 0} className="padding left-1 right-1">
                <Box
                  component="h1"
                  color="primary.main"
                  sx={{ marginTop: 0, marginBottom: "0.5rem", fontSize: { xs: "1.4rem", sm: "1.8rem", md: "2.2rem", lg: "2.4rem", xl: "2.6rem", fontFamily:"" } }}
                >
                  {t("customer_service")}
                </Box>
                <Typography component="small" sx={{ marginBottom: "1rem", display:"block" }} >
                  {t("contact_subtext")}
                </Typography>
                <p id="required-description" className="margin bottom-0">
                  <Box component="small" color="error.main">
                    *
                  </Box>
                  <small> {t("required_field")}</small>
                </p>
                <MyTextField
                  value={firstName}
                  autoComplete="given-name"
                  maxLength={100}
                  regExp={ValidTextRegExp}
                  required={true}
                  label={t("firstname")}
                  userFieldName={true}
                  helperText={t(firstNameErr)}
                  onChange={setFirstName}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      validate();
                    }
                  }}
                />
                <MyTextField
                  value={surName}
                  autoComplete="family-name"
                  maxLength={100}
                  regExp={ValidTextRegExp}
                  required={true}
                  label={t("surname")}
                  userFieldName={true}
                  helperText={t(surNameErr)}
                  onChange={setSurName}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      validate();
                    }
                  }}
                />
                <MyTextField
                  value={phoneNumber}
                  autoComplete="tel"
                  maxLength={15}
                  regExp={ValidTextRegExp}
                  required={false}
                  label={t("mobile_number")}
                  helperText={t(phoneNumberErr)}
                  userFieldName={true}
                  inputMode="numeric"
                  onChange={(e) => {
                    setPhoneChanged(true);
                    setPhoneNumber(e);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      validate();
                    }
                  }}
                />
                <MyTextField
                  value={email}
                  autoComplete="email"
                  maxLength={100}
                  regExp={ValidTextRegExp}
                  label={t("email")}
                  required={true}
                  userFieldName={true}
                  helperText={t(emailErr)}
                  onChange={(e) => {
                    setEmailChanged(true);
                    setEmail(e);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      validate();
                    }
                  }}
                />
                <MyTextField
                  maxLength={1000}
                  helperText={t(commentErr)}
                  regExp={ValidTextRegExp}
                  value={comment}
                  required={true}
                  onChange={setComment}
                  label={t("comment_and_indicate_if_want_to_be_contacted")}
                  multiline={true}
                  rows={4}
                />
                <h2 className="margin bottom-0" style={{ fontSize: "1.25rem" }}>
                  {t("do_you_need_to_attach_receipts_or_certificates")}
                </h2>
                <p>
                  <small>{t("add_up_to_3_attachments_25_MB_per_file")}</small>
                </p>
                {error && (
                  <Alert variant="filled" severity="error" className="margin top-1 bottom-1">
                    {t(error)}
                  </Alert>
                )}
                {(!attachments || attachments.length > 0) && (
                  <>
                    <hr className="margin top-0 bottom-0" aria-hidden={true} />
                    {attachments.map((item, key) => (
                      <Grid container key={key} justifyContent="space-between" alignItems={"center"}>
                        <Grid xs={10} sm={10} item color="primary.main" container wrap="nowrap" alignItems={"center"} style={{ overflowX: "hidden" }}>
                          <AttachFile fontSize="small" color="primary.main" style={{ marginRight: "1rem", marginLeft: "0.5rem" }} />
                          <Box whiteSpace={"nowrap"} component="small" color="primary.main">
                            {item.name}
                          </Box>
                        </Grid>
                        <Grid item>
                          <IconButton aria-label={t("delete")} onClick={(e) => removeAttachment(item.name)}>
                            <Delete />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                  </>
                )}
                {(!attachments || attachments.length < MaxNumberOfAttachments) && (
                  <>
                    <hr className="margin top-0 bottom-0" aria-hidden={true} />
                    <Button
                      aria-haspopup="dialog"
                      variant="text"
                      className="button secondary w-100 justify-content-start"
                      style={{
                        textTransform: "unset",
                      }}
                      sx={{
                        "& span": {
                          color: "primary.main",
                        },
                        "& svg": {
                          color: "primary.main",
                        },
                      }}
                      onClick={handleClick}
                    >
                      <AttachFile fontSize="small" style={{ marginRight: "1rem" }} />
                      <span>{t("add_receipt_or_certificate")}</span>
                    </Button>
                    <input
                      accept={context.allowFileUploadExtension.map((extension) => `.${extension}`.toLowerCase()).join()}
                      type="file"
                      ref={hiddenFileInput}
                      onChange={handleChangeAttachment}
                      onClick={(event) => {
                        event.target.value = null;
                      }}
                      style={{ display: "none" }}
                    />
                  </>
                )}
                <hr className="margin top-0 bottom-0" aria-hidden={true} />
                <MyCheckbox
                  label={t("declare_information_personal_data")}
                  helperText={t(declareInformationErr)}
                  checked={context.declareInformation}
                  onChange={context.changeDeclareInformation}
                  className="margin top-1"
                  style={{ marginBottom: "0.5rem" }}
                />
                <Typography sx={{ flex: "1 1 100%" }} id="tableTitle" component="p">
                  {t("read_about_how_data_processed")}
                  <Box style={{ width: "100%" }} component="a" color="primary.main" target="_blank" href={VLPersonalDataLink}>
                    {t("read_about_how_data_processed_link")}
                  </Box>
                  {`.`}
                </Typography>
                <MyButton style={{ float: "right" }} className="button primary" text={t("submit")} onClick={validate} />
              </div>
              <div hidden={context.activeSlide !== 1}>
                <Grid container spacing={3} style={{ paddingLeft: "1rem", paddingRight: "1rem" }}>
                  <Grid item xs={12} sm={12}>
                    <img src={Logo} alt="VL, logo" style={{ maxWidth: "50%", width: "50%" }} />
                  </Grid>
                  <Grid item>
                    <div dangerouslySetInnerHTML={{ __html: t("thank_you_for_your_complaint", { casenumber: context.caseNumber }) }} />
                  </Grid>
                  <Grid item>
                    <MyButton className="button primary margin top-0" text={t("submit_another_claim")} onClick={() => context.changeActiveSlide(0)} />
                  </Grid>
                </Grid>
              </div>
            </Slider>
            <div hidden={context.activeSlide !== 0}>
              <Grid item container justifyContent="center" spacing={2}>
                {Object.keys(lngs).map((lng) => (
                  <Grid item key={lng}>
                    <Typography
                      className="selector"
                      variant="overline"
                      display="block"
                      lang={lng}
                      component="button"
                      gutterBottom
                      onClick={() => {
                        i18n.changeLanguage(lng);
                        document.documentElement.lang = lng;
                      }}
                      style={{ fontWeight: i18n.language === lng ? "bold" : "normal" }}
                    >
                      {lngs[lng].nativeName}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </div>
          </Grid>
        </Grid>
      )}
    </MyContext.Consumer>
  );
};
